import { z } from 'zod';
import { counter } from './counts.js';
import { emailBodySchema } from './emailBody.js';
import { pageOf } from './paging.js';
/**
 * Campaigns, over the wire.
 *
 * A campaign is created once and keeps its id for the life of the send (I1), so
 * nothing here mints one: the client asks the server to create a campaign and
 * is given the id back, and every later call names it.
 */
/** Not exported: every consumer reaches these through `CampaignDto['status']`. */
const campaignStatusSchema = z.enum([
    'draft',
    'scheduled',
    'queued',
    'sending',
    'paused',
    'sent',
    'failed',
]);
export const campaignSchema = z.object({
    campaignId: z.string(),
    name: z.string(),
    status: campaignStatusSchema,
    /**
     * Null is the whole subscribed list, not a missing value. A campaign to
     * everyone is ordinary, and it is the same audience as a segment with no
     * rules.
     */
    segmentId: z.string().nullable(),
    /** Carried so a list can name the audience without a second request. */
    segmentName: z.string().nullable(),
    fromEmail: z.string(),
    fromName: z.string(),
    subject: z.string(),
    /**
     * How many messages were queued.
     *
     * Null before the campaign is queued, because there is no audience until
     * then — a segment is a question, and the answer is the set of contacts it
     * matched at the moment of queueing. Showing a live count on a draft would be
     * a different number from the one that eventually sends.
     */
    queuedCount: counter.nullable(),
    createdAt: z.string().datetime(),
    /**
     * When it was queued, and null before that.
     *
     * Stored as `frozen_at`: the template is frozen at exactly the moment the
     * campaign is queued, and one timestamp for one event cannot disagree with
     * itself the way two would.
     */
    queuedAt: z.string().datetime().nullable(),
});
export const campaignListSchema = z.object({ campaigns: z.array(campaignSchema) });
/**
 * Everything a campaign is written from.
 *
 * One schema for creating and for rewriting, because they accept exactly the
 * same thing — and two names for one shape is two places for a limit to drift,
 * with the looser one deciding what actually reaches the database.
 *
 * `body` is tagged with how it was written — prose from a textarea, or a
 * design from the block builder. `campaignEmailTemplate` turns either into the
 * html and text parts stored on the campaign, which is why there is one field
 * rather than two: two would let a campaign carry both and say nothing about
 * which one sends.
 */
export const campaignDraftSchema = z.object({
    name: z.string().trim().min(1, 'a campaign needs a name').max(120),
    /** Absent or null is the whole subscribed list. */
    segmentId: z.string().uuid().nullable().optional(),
    fromName: z.string().trim().max(120),
    fromEmail: z.string().trim().email('the from address is not an email address').max(200),
    /**
     * Where a reply goes, when it is not the From address.
     *
     * Empty rather than absent, because a form sends what is in the box and an
     * empty box means "no separate reply address" — a distinct answer from
     * "leave it as it was".
     */
    replyTo: z.union([
        z.string().trim().email('the reply address is not an email address').max(200),
        z.literal(''),
    ]),
    subject: z.string().trim().min(1, 'a campaign needs a subject').max(200),
    body: emailBodySchema,
});
export const createCampaignResponseSchema = z.object({ campaignId: z.string() });
/**
 * Queueing answers one of three ways, and a caller acts on each.
 *
 * `already-queued` is a success, not a conflict. Someone who clicks twice, or
 * whose first request timed out and was retried, must be told what happened
 * rather than shown an error next to a campaign that is sending perfectly well.
 *
 * `empty-audience` refuses rather than queueing nothing. A campaign that goes
 * out to nobody and reports itself sent is the kind of quiet success that stops
 * people trusting the numbers.
 *
 * `over-allowance` refuses the whole campaign rather than queueing what fits.
 * A send split across two months leaves somebody unable to say which of their
 * contacts received it, and the half that went would have been chosen by
 * whatever order the audience query happened to return.
 */
export const queueCampaignResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('queued'), queued: z.number().int().min(1) }),
    z.object({ status: z.literal('already-queued'), queued: counter }),
    z.object({ status: z.literal('empty-audience') }),
    z.object({
        status: z.literal('over-allowance'),
        /** What the campaign would have taken, and what is left this month. */
        needed: counter,
        remaining: counter,
    }),
]);
/**
 * What happened to a campaign.
 *
 * Two different questions, kept apart because they answer at different times.
 * `outcomes` is the send log: what the provider said about each attempt, and it
 * only grows. `queue` is the live state of the rows still to be dealt with, and
 * it drains. A campaign is finished when the queue holds nothing but `done`,
 * `unknown` and `dead`.
 *
 * There is no open or click rate, and no bounce or complaint count. Opens and
 * clicks need a tracking pixel, which this product deliberately does not have.
 * Bounces and complaints arrive by webhook minutes or days later and carry the
 * provider’s own message id rather than a campaign, so attributing them to one
 * send would be a guess. They land on the suppression list, which is where the
 * screen points.
 */
/**
 * What the provider answered, counted.
 *
 * One schema for the campaign's totals and for each bar of its timeline, so the
 * bars add up to the totals rather than being two shapes that can disagree
 * about what they are counting.
 */
const outcomeCountsSchema = z.object({
    accepted: counter,
    /** The provider had seen this message id before. Not a second delivery. */
    duplicate: counter,
    rejected: counter,
    failed: counter,
    /**
     * The provider never gave a clear answer, so the mail may well have been
     * delivered. Never retried, and never to be shown as a failure. (I7)
     */
    unknown: counter,
});
/**
 * One slice of the send.
 *
 * `at` is the start of the slice; its width is not carried per bar because
 * every bar of one timeline has the same one, and a width repeated per row is a
 * width that can differ per row. The browser reads it from the gap between two
 * bars.
 */
const sendBucketSchema = z.object({ at: z.string().datetime(), counts: outcomeCountsSchema });
export const campaignReportSchema = z.object({
    campaign: campaignSchema,
    /** From `send_log`. Append-only, so these only ever grow. */
    outcomes: outcomeCountsSchema,
    /** From `send_queue`. The work still in front of the workers. */
    queue: z.object({
        queued: counter,
        claimed: counter,
        done: counter,
        unknown: counter,
        /** Out of retries. Visible here because a silent dead letter is worthless. */
        dead: counter,
    }),
    /**
     * Unsubscribes this campaign caused.
     *
     * Attributable, unlike a bounce: the one-click link carries a per-recipient
     * token, and the consent log files the event under the campaign that prompted
     * it.
     */
    unsubscribed: counter,
    /**
     * The send over the time it took, oldest slice first.
     *
     * Empty for a campaign nothing has been attempted for. Every slice between
     * the first and the last is present, including the empty ones: a series that
     * omitted them would close up a quiet twenty minutes and draw continuous
     * sending that never happened.
     */
    timeline: z.array(sendBucketSchema),
});
/**
 * One person a campaign went to, and what became of it.
 *
 * Two states rather than one verdict. `queue` is where the row stands now;
 * `outcome` is what the provider said the first time the message was
 * attempted, and the send log keeps only that first answer (I3) — so a message
 * that failed once and then went out reads `failed` here and `done` there.
 * Merging them would mean choosing which of two true things to hide.
 */
export const campaignRecipientSchema = z.object({
    email: z.string(),
    /** Empty for a contact with no name. The address is what they do have. */
    name: z.string(),
    queue: z.enum(['queued', 'claimed', 'done', 'unknown', 'dead']),
    /** Null means never attempted, which is not the same as no answer. */
    outcome: z.enum(['accepted', 'duplicate', 'rejected', 'failed', 'unknown']).nullable(),
    /** The latest error the queue recorded. Null while nothing has gone wrong. */
    reason: z.string().nullable(),
    /** When the provider answered. Null for a recipient not yet attempted. */
    answeredAt: z.string().datetime().nullable(),
});
export const campaignRecipientPageSchema = pageOf(campaignRecipientSchema);
/**
 * A campaign's content, for an editor to open.
 *
 * `body` is null for a campaign written before the source was stored. That is
 * a real state rather than an empty one — the rendered message exists and what
 * produced it does not — and the composer says so instead of opening an empty
 * editor over content it cannot show.
 *
 * Its kind decides which editor opens: a textarea for prose, the builder for a
 * design.
 */
export const campaignContentSchema = z.object({
    campaignId: z.string(),
    name: z.string(),
    status: campaignStatusSchema,
    /** Null is everyone subscribed, as everywhere else. */
    segmentId: z.string().nullable(),
    fromEmail: z.string(),
    fromName: z.string(),
    /** Empty when replies go to the From address. */
    replyTo: z.string(),
    subject: z.string(),
    body: emailBodySchema.nullable(),
});
/**
 * Rewriting answers one of two ways.
 *
 * `not-a-draft` is the campaign having been queued since the editor was
 * opened. It is not a validation failure and not a permission problem: the
 * message is already going out, and changing it now would mean two different
 * emails went under one campaign id.
 */
export const rewriteCampaignResponseSchema = z.discriminatedUnion('status', [
    z.object({ status: z.literal('rewritten') }),
    z.object({ status: z.literal('not-a-draft') }),
]);
/**
 * A preview of what would be sent.
 *
 * Rendered by the server rather than by the browser, and that is the point: it
 * comes out of the same function that builds a campaign's markup at create and
 * at rewrite. A preview drawn by a second renderer is a preview that can
 * disagree with the message — and the browser holds a cached bundle that may be
 * older than the server, so "the same code" would not stay true for long.
 *
 * The merge placeholders are filled with an example recipient, because a
 * preview showing `{{name}}` tells you nothing about whether the greeting
 * reads properly.
 */
export const campaignPreviewRequestSchema = z.object({
    subject: z.string().trim().min(1).max(200),
    body: emailBodySchema,
});
export const campaignPreviewResponseSchema = z.object({
    subject: z.string(),
    html: z.string(),
    text: z.string(),
});
/**
 * What a person sees before they send to several thousand people.
 *
 * Sending is the only irreversible thing in this product, and the things that
 * make it go badly — an unverified domain, an audience of nobody, a message
 * nobody has seen arrive — are all knowable beforehand and all invisible on a
 * list.
 */
export const preSendCheckSchema = z.object({
    id: z.string(),
    title: z.string(),
    /** Only `blocking` stops the send. `attention` is said plainly and obeyed by nobody but the reader. */
    outcome: z.enum(['ready', 'attention', 'blocking']),
    detail: z.string(),
});
/**
 * When a campaign should go out, as the browser sends it.
 *
 * A discriminated union rather than four optional fields, matching the domain
 * and the column: the fields that belong to each strategy are meaningless for
 * the other, and optionality would let the client ask for "at one moment, with
 * a fallback timezone, and no moment".
 *
 * The strings are validated for shape here and for meaning in the domain —
 * a time in the past and a timezone this platform cannot read are both real
 * refusals that a regular expression has no opinion about.
 */
export const campaignScheduleSchema = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('at-once'),
        /** An instant, in UTC, as ISO 8601. */
        at: z.string().datetime(),
    }),
    z.object({
        kind: z.literal('recipient-local'),
        /** A wall clock with no zone: `YYYY-MM-DDTHH:mm`. */
        localAt: z
            .string()
            .regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, 'a date and a time, without a zone'),
        /** Used for contacts with no zone of their own. An IANA name. */
        fallbackTimezone: z.string().min(1).max(64),
    }),
]);
export const campaignReadinessSchema = z.object({
    name: z.string(),
    subject: z.string(),
    /** Counted now: a segment is a question answered at the moment of sending. */
    audience: counter,
    checks: z.array(preSendCheckSchema),
    /** Computed on the server, so the screen and the route cannot disagree about it. */
    canSend: z.boolean(),
    /** The moment already set, or absent for a campaign that has none. */
    schedule: campaignScheduleSchema.optional(),
});
/**
 * A test send is queued rather than sent on the spot.
 *
 * It goes through the same queue and the same worker as everything else (I2),
 * which is what makes it a test of the real path rather than of a second one —
 * so the answer is "queued", and the screen says to check the inbox.
 */
export const testSendCampaignResponseSchema = z.object({
    status: z.enum(['queued', 'not-found']),
});
export const scheduleCampaignRequestSchema = z.object({ schedule: campaignScheduleSchema });
/**
 * What scheduling answered.
 *
 * `invalid` carries the domain's own sentences, so the screen says the same
 * thing the server decided rather than a paraphrase that can drift from it.
 */
export const scheduleCampaignResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('scheduled'),
        /** When it will have reached everybody, which a local-time send spreads out. */
        finishedBy: z.string().datetime(),
        /** How many of the audience have no timezone and will use the fallback. */
        usingFallback: z.number().int(),
        /** The audience as it stands now, so the screen can say who this is for. */
        audience: z.number().int(),
    }),
    z.object({ status: z.literal('invalid'), problems: z.array(z.string()) }),
    z.object({ status: z.literal('not-a-draft') }),
]);
//# sourceMappingURL=campaigns.js.map